import React, {Fragment} from 'react'
import {connect} from 'react-redux'
import styles from './SmartExport.scss'
import {Icon} from 'common/Icon'
import {PopoverLinkMenu, PopoverLinkMenuItem, PopoverLinkMenuHeading} from 'common/styleguide/PopoverLinkMenu'
import {CTA} from 'common/styleguide/CTA'
import {formatsForObjectType, SMART_EXPORT_DEFAULT, SMART_EXPORT_FILE_DOWNLOAD, SMART_EXPORT_PHONE_PUSH} from 'lib/exportFormats'
import {EXPORT_DIALOG} from '../../dialogs/ExportDialog'
import {QuickExportDialog} from '../../dialogs/QuickExportDialog'
import p from 'prop-types'
import {userIsAdmin} from 'lib/utils'
import {setPreference} from 'lib/api/currentUser'
import fetchJSON from 'lib/fetchJSON'
import {CircleSpinner} from 'common/spinners'

export class SmartExport extends React.PureComponent {
  static propTypes = {
    currentUser: p.object.isRequired,
    r: p.object.isRequired
  }

  constructor (props) {
    super(props)
    this.state = {
      expanded: false,
      pushPending: false,
      pushSent: false,
      pushError: false
    }
  }

  toggleDropdown = () => {
    this.setState((state) => ({expanded: !state.expanded}))
  }

  getExportState = () => {
    const {currentUser} = this.props
    return (currentUser && currentUser.preferences.get('smart_export_state')) || SMART_EXPORT_DEFAULT
  }

  getStickyFileFormat = () => {
    const {currentUser} = this.props
    return (currentUser && currentUser.preferences.get('smart_export_file_format')) || 'fit'
  }

  buttonClicked = () => {
    const {sendToDevice} = this.props
    const {pushPending} = this.state
    const exportState = this.getExportState()

    if (pushPending) { return }

    if (exportState === SMART_EXPORT_DEFAULT) {
      sendToDevice()
      this.setState({pushPending: false, pushSent: false, pushError: false})
    }

    if (exportState === SMART_EXPORT_FILE_DOWNLOAD) {
      this.setState({showQuickExport: true})
    }

    if (exportState === SMART_EXPORT_PHONE_PUSH) {
      this.sendPhonePush()
    }
  }

  sendPhonePush = () => {
    const {r, setPreference} = this.props

    this.setState({
      expanded: false,
      pushPending: true,
      pushSent: false,
      pushError: false
    })
    fetchJSON(`/routes/${r.id}/send_push_notification`, {}, {method: 'POST'})
      .then(() => {
        this.setState({pushPending: false, pushSent: true, pushError: false})
        setPreference('smart_export_state', SMART_EXPORT_PHONE_PUSH)
      })
      .catch(() => {
        this.setState({pushPending: false, pushSent: false, pushError: true})
      })
  }

  hideQuickExport = () => {
    this.setState({
      showQuickExport: false,
      fileFormatOverride: null
    })
  }

  showFileExportDialog = () => {
    const {showDialog} = this.props
    showDialog(EXPORT_DIALOG)
    this.setState({
      expanded: false,
      pushPending: false,
      pushError: false,
      pushSent: false
    })
  }

  downloadFitClicked = () => {
    this.setState({
      fileFormatOverride: 'fit',
      showQuickExport: true,
      expanded: false,
      pushPending: false,
      pushError: false,
      pushSent: false
    })
  }

  resetPreferences = () => {
    setPreference('smart_export_state', null)
    setPreference('smart_export_show_phone_push_option', null)
    this.setState({expanded: false})
    window.location.reload()
  }

  render () {
    const {
      expanded,
      showQuickExport,
      fileFormatOverride,
      pushSent,
      pushPending,
      pushError
    } = this.state
    const {
      r,
      showDialog,
      sendToDevice,
      currentUser
    } = this.props
    const stickyFileFormat = this.getStickyFileFormat()
    const originalExportState = this.getExportState()

    // If a push is or was in progress, show we're in 'phone_push', otherwise proceed as normal.
    // this is here so that sending a push from the dropdown will still show the right in-progress messages
    // Clicking any other button or item from the a dropdown should clear all push statuses
    const exportState = (pushPending || pushError || pushSent) ? SMART_EXPORT_PHONE_PUSH : originalExportState

    // being super defensive here becuase pref map key change caused a bunch of problems
    const formats = formatsForObjectType('route')
    const f = formats.get(stickyFileFormat) || formats.get('fit')

    const showPhonePushOption = currentUser.preferences.get('smart_export_show_phone_push_option')

    return (
      <div className={styles.SmartExport}>
        <CTA margins={false} className={styles.primary} onClick={this.buttonClicked} data-tip={pushError} title={pushError ? 'Click to re-send' : ''}>
          {exportState === SMART_EXPORT_DEFAULT && 'Send to Device'}
          {exportState === SMART_EXPORT_FILE_DOWNLOAD && `Export ${f.ext.toUpperCase()} File`}
          {exportState === SMART_EXPORT_PHONE_PUSH && <Fragment>
            {pushPending && <Fragment>
              <CircleSpinner size={15} className={styles.spinner} /> Sending route
            </Fragment>}
            {pushSent && <Fragment>
              <Icon i='md-checkmark' size={15} /> Notification Sent
            </Fragment>}
            {pushError && 'Phone Push Error'}
            {!pushSent && !pushPending && !pushError && 'Send to Phone'}
          </Fragment>}
        </CTA>
        <CTA ref={r => { this._menuAnchor = r }} onClick={this.toggleDropdown} margins={false} className={styles.more}>
          <Icon i='ios-more-rotated' />
        </CTA>
        {expanded && <PopoverLinkMenu
          target={this._menuAnchor}
          placement='bottom'
          onRequestClose={this.toggleDropdown}>
          {exportState === SMART_EXPORT_DEFAULT && <Fragment>
            {showPhonePushOption && <PopoverLinkMenuItem onClick={this.sendPhonePush}>Send to phone</PopoverLinkMenuItem>}
            <PopoverLinkMenuItem onClick={this.downloadFitClicked}>Download .FIT file</PopoverLinkMenuItem>
            <PopoverLinkMenuItem onClick={this.showFileExportDialog}>More export options</PopoverLinkMenuItem>
          </Fragment>}
          {exportState === SMART_EXPORT_FILE_DOWNLOAD && <Fragment>
            {showPhonePushOption && <PopoverLinkMenuItem onClick={this.sendPhonePush}>Send to phone</PopoverLinkMenuItem>}
            <PopoverLinkMenuItem onClick={() => sendToDevice()}>Send to a different device</PopoverLinkMenuItem>
            <PopoverLinkMenuItem onClick={this.showFileExportDialog}>More export options</PopoverLinkMenuItem>
          </Fragment>}
          {exportState === SMART_EXPORT_PHONE_PUSH && <Fragment>
            <PopoverLinkMenuItem onClick={() => sendToDevice()}>Send to a different device</PopoverLinkMenuItem>
            <PopoverLinkMenuItem onClick={this.showFileExportDialog}>More export options</PopoverLinkMenuItem>
          </Fragment>}
          {userIsAdmin && <Fragment>
            <PopoverLinkMenuHeading>RWGPS Admin</PopoverLinkMenuHeading>
            <PopoverLinkMenuItem onClick={this.resetPreferences}>Clear state</PopoverLinkMenuItem>
          </Fragment>}
        </PopoverLinkMenu>}
        {showQuickExport && <QuickExportDialog
          route={r}
          format={fileFormatOverride || stickyFileFormat}
          showDialog={showDialog}
          onRequestClose={this.hideQuickExport} />}
      </div>
    )
  }
}

const mapStateToProps = (state) => {
  return {
    currentUser: state.currentUser
  }
}

const mapDispatchToProps = {
  setPreference: setPreference
}

export const SmartExportContainer = connect(
  mapStateToProps,
  mapDispatchToProps
)(SmartExport)
